"use client";

import authImage from "@/assets/auth/auth-img.png";
import authElement from "@/assets/auth/auth-left-footer.png";
import authElement2 from "@/assets/auth/auth-title-blur.png";
import { useSettings } from "@/context/settingsContext";
import { useT } from "@/context/TranslationContext";
import { useUser } from "@/context/UserContext";
import ApiPath from "@/network/api/apiPath";
import NetworkService from "@/network/service/networkService";
import TokenService from "@/network/service/tokenService";
import { getSettingValue } from "@/utils/utils";
import { Icon } from "@iconify/react";
import Cookies from "js-cookie";
import Image from "next/image";
import Link from "next/link";
import { usePathname, useRouter } from "next/navigation";
import { useState } from "react";
import LanguageDropdown from "../dashboard/UserDropdown/LanguageDropdown";
import { Spinner } from "../ui/spinner";

const Login = () => {
  const [showPassword, setShowPassword] = useState(false);
  const pathname = usePathname();
  const network = new NetworkService();
  const tokenService = new TokenService();
  const { refreshUser } = useUser();
  const [errors, setErrors] = useState({});
  const [form, setForm] = useState({
    email: "",
    password: "",
    rememberMe: false,
  });
  const [loading, setLoading] = useState(false);
  const router = useRouter();
  const { settings } = useSettings();
  const siteFaVerification = getSettingValue(settings, "fa_verification");
  const siteLogo = getSettingValue(settings, "site_logo");
  const siteLogoHeight = getSettingValue(settings, "site_logo_height");
  const t = useT();

  // console.log("site verification", siteFaVerification);

  // handle form validation
  const handleFormValidation = () => {
    let newErrors = {};

    if (!form.email) {
      newErrors.email = "Please fill Email";
    } else {
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(form.email)) {
        newErrors.email = "Please enter a valid email address";
      }
    }

    if (!form.password) {
      newErrors.password = "Please fill Password";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // handle login
  const handleLoginSubmit = async (e) => {
    e.preventDefault();
    if (!handleFormValidation()) return;

    try {
      setLoading(true);
      const requestBody = { email: form.email, password: form.password };
      const res = await network.globalPost(ApiPath.login, requestBody);
      if (res.status === "completed") {
        const token = res.data.data.token;
        await network.tokenService.saveAccessToken(token);
        await refreshUser();
        await setRedirectPath();
      }
    } finally {
      setLoading(false);
    }
  };

  // after login get user data and do condition where to go
  const setRedirectPath = async () => {
    try {
      const res = await network.get(ApiPath.user);
      if (res.status === "completed") {
        const steps = res?.data?.data?.boarding_steps;
        if (steps?.completed === true) {
          Cookies.set("kycVerify", "true");
          Cookies.set("personalInfo", "true");
          if (siteFaVerification === "1" && res?.data?.data?.two_fa === true) {
            router.push("/auth/login/two-fa-verification-page");
          } else {
            router.push("/dashboard");
          }
        } else if (steps?.personal_info === true) {
          router.push("/auth/step-verification/success-status");
        } else {
          router.push("/auth/step-verification/success-status");
        }
      }
    } catch (error) {
      console.error("Error fetching user data:", error);
      router.push("/dashboard");
    }
  };

  return (
    <div className="auth-full-page h-screen overflow-hidden">
      <div className="auth-page flex lg:flex-row flex-col h-full">
        <div className="left w-full lg:w-1/2">
          <div className="auth-left-content h-full flex flex-col items-center relative">
            <div className="max-w-full sm:max-w-[460px] xl:max-w-[560px] 2xl:max-w-[640px] 3xl:max-w-[721px] h-screen border-l-0 sm:border-l border-r-0 sm:border-r border-user-input-border relative">
              <div className="absolute top-3 right-3">
                <LanguageDropdown authpage={true} />
              </div>
              <div className="auth-left-content-inner">
                <Link
                  href="/"
                  className={`block logo h-[${siteLogoHeight}px] w-auto mb-8 sm:mb-[40px] lg:mb-[60px] 2xl:mb-[90px]`}
                >
                  {siteLogo ? (
                    <Image
                      src={siteLogo}
                      height={40}
                      width={100}
                      alt="Logo"
                      unoptimized
                      className="h-full w-auto"
                    />
                  ) : (
                    <div className="h-[30px] w-[100px] bg-gray-100 animate-pulse rounded"></div>
                  )}
                </Link>
                <div className="title-and-button px-0 2xl:px-[75px] relative mb-8 sm:mb-[40px] lg:mb-[50px]">
                  <div className="text-center">
                    <h2 className="text-[24px] sm:text-[30px] font-bold text-user-text mb-3 text-center">
                      {t("authPages.welcomeText")}
                    </h2>
                    <p className="text-[14px] text-user-paragraph font-semibold text-center">
                      {t("authPages.welcomeSubText")}
                    </p>
                    <div className="inline-flex justify-center bg-[#F1EDFF] rounded-[16px] p-1 mt-[30px]">
                      <Link
                        href="/auth/login"
                        className={`${
                          pathname.startsWith("/auth/login")
                            ? "bg-white text-user-text"
                            : "bg-transparent text-user-paragraph"
                        } inline-flex items-center justify-center px-[24px] h-[40px] rounded-[12px] text-[14px] font-bold`}
                      >
                        {t("authPages.login")}
                      </Link>
                      <Link
                        href="/auth/register"
                        className={`${
                          pathname.startsWith("/auth/register")
                            ? "bg-white text-user-text"
                            : "bg-transparent text-user-paragraph"
                        } inline-flex items-center justify-center px-[24px] h-[40px] rounded-[12px] text-[14px] font-bold`}
                      >
                        {t("authPages.signUp")}
                      </Link>
                    </div>
                  </div>
                  <div className="bg-blur top-[-110px] absolute left-1/2 -translate-x-1/2 z-[-1]">
                    <div className="w-[541px] h-[282px]">
                      <Image
                        src={authElement2}
                        width={1000}
                        height={1000}
                        alt="Background Blur"
                        className="w-[541px] h-[282px]"
                      />
                    </div>
                  </div>
                </div>
                <div className="w-full">
                  <form onSubmit={handleLoginSubmit}>
                    <div className="grid grid-cols-12 gap-3.5 sm:gap-5">
                      <div className="col-span-12">
                        <label htmlFor="email" className="user-label">
                          {t("authPages.email")}{" "}
                          <span className="text-red-500">*</span>
                        </label>
                        <div>
                          <div className="relative">
                            <input
                              type="email"
                              id="email"
                              name="email"
                              className={`user-input user-input-right peer ${
                                errors.email
                                  ? "!border-user-error focus:!border-user-error"
                                  : ""
                              }`}
                              value={form.email}
                              onChange={(e) =>
                                setForm({ ...form, email: e.target.value })
                              }
                            />
                            <div className="icon absolute rtl:left-4 ltr:right-4 top-1/2 -translate-y-1/2 text-[rgba(45,45,45,0.44)] peer-focus:text-[rgba(116,69,255,0.60)]">
                              <Icon
                                icon="basil:envelope-solid"
                                width="20"
                                height="20"
                              />
                            </div>
                          </div>
                          {errors.email && (
                            <p className="text-[13px] text-user-error mt-1">
                              {errors.email}
                            </p>
                          )}
                        </div>
                      </div>
                      <div className="col-span-12">
                        <label htmlFor="password" className="user-label">
                          {t("authPages.password")}{" "}
                          <span className="text-user-error">*</span>
                        </label>
                        <div>
                          <div className="relative">
                            <input
                              type={showPassword ? "text" : "password"}
                              id="password"
                              name="password"
                              className={`user-input user-input-right peer ${
                                errors.password
                                  ? "!border-user-error focus:!border-user-error"
                                  : ""
                              }`}
                              value={form.password}
                              onChange={(e) =>
                                setForm({ ...form, password: e.target.value })
                              }
                            />
                            <button
                              type="button"
                              onClick={() => setShowPassword(!showPassword)}
                              className="icon absolute rtl:left-4 ltr:right-4 top-1/2 -translate-y-1/2 text-[rgba(45,45,45,0.44)] peer-focus:text-[rgba(116,69,255,0.60)]"
                            >
                              <Icon
                                icon={
                                  showPassword
                                    ? "basil:eye-solid"
                                    : "basil:eye-closed-solid"
                                }
                                width="20"
                                height="20"
                              />
                            </button>
                          </div>
                          {errors.password && (
                            <p className="text-[13px] text-red-500 mt-1">
                              {errors.password}
                            </p>
                          )}
                        </div>
                        <div className="flex justify-between items-center mt-3">
                          <div className="flex items-center gap-2">
                            <input
                              type="checkbox"
                              className="checkbox-colored"
                              id="colored-checkbox"
                              checked={form.rememberMe}
                              onChange={(e) =>
                                setForm({
                                  ...form,
                                  rememberMe: e.target.checked,
                                })
                              }
                            />
                            <label
                              htmlFor="colored-checkbox"
                              className="text-[14px] text-user-paragraph font-semibold cursor-pointer"
                            >
                              {t("authPages.rememberMe")}
                            </label>
                          </div>
                          <div>
                            <Link
                              href="/auth/login/forget-password"
                              className="text-[13px] text-user-primary font-semibold transition-all hover:text-[#6031E9]"
                            >
                              {t("authPages.forgetPassword")}
                            </Link>
                          </div>
                        </div>
                      </div>
                    </div>
                    <button
                      type="submit"
                      className="group primary-button w-full mt-5 sm:mt-[30px] lg:mt-[40px]"
                      disabled={loading}
                    >
                      <span className="primary-button-hover-effect"></span>
                      <span className="primary-button-text">
                        {loading ? (
                          <div className="flex items-center gap-2">
                            <Spinner /> {t("authPages.signingIn")}
                          </div>
                        ) : (
                          t("authPages.signIn")
                        )}
                      </span>
                    </button>
                  </form>
                </div>
                <div className="path-switch mt-[40px] lg:mt-[60px]">
                  <p className="text-center text-[13px] font-semibold text-user-paragraph">
                    {t("authPages.notRegisteredYet")}{" "}
                    <Link href="/auth/register" className="text-user-primary">
                      {t("authPages.createAnAccount")}
                    </Link>
                  </p>
                </div>
              </div>
            </div>
            <div className="absolute bottom-0 rtl:right-0 ltr:left-0 z-[-1] w-full">
              <Image
                src={authElement}
                width={1000}
                height={1000}
                alt="Auth Element"
                className="w-full h-full object-cover"
              />
            </div>
          </div>
        </div>
        <div className="right w-1/2 h-screen fixed rtl:left-0 ltr:right-0 top-0 lg:block hidden">
          <div className="auth-image w-full h-full">
            <Image
              src={authImage}
              width={1000}
              height={1000}
              alt="Auth Image"
              className="w-full h-full object-cover"
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default Login;
